import { Box, ButtonBase, Typography } from '@shared/ui';
import type React from 'react';
import googleCalendarIcon from '../../assets/icons/GoogleCalendar.png';
import iCalCalendarIcon from '../../assets/icons/ICalCalendar.svg';
import outlookCalendarIcon from '../../assets/icons/OutlookCalendar.png';

interface AddCalendarAccountRowProps {
  disabled?: boolean;
  onAdd: (provider: string) => void;
  availableProviders?: string[];
}

const CALENDAR_PROVIDER_ACTIONS = [
  {
    provider: 'google-calendar',
    label: 'Google Calendar',
    ariaLabel: 'Add Google Calendar account',
    iconSrc: googleCalendarIcon,
  },
  {
    provider: 'microsoft-calendar',
    label: 'Outlook',
    ariaLabel: 'Add Outlook account',
    iconSrc: outlookCalendarIcon,
  },
  {
    provider: 'icloud-calendar',
    label: 'iCal',
    ariaLabel: 'Add iCal account',
    iconSrc: iCalCalendarIcon,
  },
] satisfies ReadonlyArray<{
  provider: string;
  label: string;
  ariaLabel: string;
  iconSrc: string;
}>;

type CalendarProviderAction = (typeof CALENDAR_PROVIDER_ACTIONS)[number];

interface CalendarProviderActionPillProps {
  action: CalendarProviderAction;
  disabled: boolean;
  onAdd: (provider: string) => void;
}

const CalendarProviderActionPill: React.FC<CalendarProviderActionPillProps> = ({
  action,
  disabled,
  onAdd,
}) => (
  <ButtonBase
    aria-label={action.ariaLabel}
    disabled={disabled}
    onClick={() => onAdd(action.provider)}
    sx={{
      alignItems: 'center',
      bgcolor: '#EDF2F0',
      borderRadius: '100px',
      color: '#505A54',
      display: 'inline-flex',
      height: 29,
      justifyContent: 'center',
      opacity: disabled ? 0.5 : 1,
      p: '4px',
      transition: 'background-color 120ms ease, opacity 120ms ease',
      '&:hover': {
        bgcolor: disabled ? '#EDF2F0' : '#E1EAE6',
      },
      '&:focus-visible': {
        outline: '2px solid #505A54',
        outlineOffset: 2,
      },
    }}
  >
    <Box
      component="span"
      sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5, px: 0.75, py: 0.375 }}
    >
      <Box
        component="img"
        alt=""
        src={action.iconSrc}
        sx={{ display: 'block', height: 21, width: 21 }}
      />
      <Box
        component="span"
        sx={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.16px', lineHeight: '18px' }}
      >
        {action.label}
      </Box>
    </Box>
  </ButtonBase>
);

const AddCalendarAccountRow: React.FC<AddCalendarAccountRowProps> = ({
  disabled = false,
  onAdd,
  availableProviders = ['google-calendar'],
}) => {
  const visibleActions = CALENDAR_PROVIDER_ACTIONS.filter((action) =>
    availableProviders.includes(action.provider)
  );

  return (
    <Box
      data-testid="add-calendar-account-row"
      sx={{
        alignItems: 'center',
        columnGap: 0.5,
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: 0.75,
      }}
    >
      <Typography
        component="span"
        sx={{
          color: '#000000',
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.15px',
          lineHeight: '29px',
        }}
      >
        Add a calendar account:
      </Typography>
      {visibleActions.map((action) => (
        <CalendarProviderActionPill
          key={action.provider}
          action={action}
          disabled={disabled}
          onAdd={onAdd}
        />
      ))}
    </Box>
  );
};

export default AddCalendarAccountRow;
